import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import axios from "axios";
import { useApiClient } from "../../useApiClient";
import { useRolesState } from "../../useRoles";
import { API_CONFIG, getApiUrl } from "../../config/api";
import { borderRadius, colors, elevation, spacing } from "../../theme";
import {
  DuplicateAuditListItem,
  DuplicateAuditListResponse,
  DuplicateAuditSummary,
  DuplicateReviewLookups,
  SaveDuplicateAuditReviewRequest,
} from "../../types/duplicateAudit";
import AuditSummaryTiles from "../../components/duplicateAudit/AuditSummaryTiles";
import AuditFilters, {
  AuditFilterValues,
  getDefaultFilters,
} from "../../components/duplicateAudit/AuditFilters";
import AuditQueueGrid from "../../components/duplicateAudit/AuditQueueGrid";
import AuditInvestigationPanel from "../../components/duplicateAudit/AuditInvestigationPanel";
import { ReviewFieldErrors } from "../../components/duplicateAudit/ReviewForm";

const DEBOUNCE_MS = 400;
const DATE_FORMAT = "YYYY-MM-DD";

interface ApiErrors {
  errors?: { field: string; error: string }[];
}

// Values of the API's sortBy / sortDirection enums.
const SORT_BY: Record<string, number> = {
  createdAtUtc: 0,
  risk: 1, // risk is derived from the score
  score: 1,
  createdBy: 2,
  newCustomer: 3,
  isReviewed: 4,
  match: 5,
};
const SORT_DIRECTION = { desc: 0, asc: 1 };

const DEFAULT_SORT_MODEL: GridSortModel = [{ field: "createdAtUtc", sort: "desc" }];

const buildQuery = (
  filters: AuditFilterValues,
  paginationModel: GridPaginationModel,
  sortModel: GridSortModel
): string => {
  const params = new URLSearchParams();
  const sort = sortModel[0];
  if (sort?.sort && sort.field in SORT_BY) {
    params.set("sortBy", String(SORT_BY[sort.field]));
    params.set("sortDirection", String(SORT_DIRECTION[sort.sort]));
  }
  params.set("page", String(paginationModel.page + 1)); // API pages are 1-based
  params.set("pageSize", String(paginationModel.pageSize));
  if (filters.from) params.set("from", filters.from.format(DATE_FORMAT));
  if (filters.to) params.set("to", filters.to.format(DATE_FORMAT));
  if (filters.createdBy.trim()) params.set("createdBy", filters.createdBy.trim());
  if (filters.minScore > 0) params.set("minScore", String(filters.minScore));
  if (filters.reviewStatus !== "All") params.set("reviewStatus", filters.reviewStatus);
  if (filters.search.trim()) params.set("search", filters.search.trim());
  return params.toString();
};

export default function DuplicateAuditWorkbench() {
  const { callApi } = useApiClient();
  const { roles, loaded: rolesLoaded } = useRolesState();
  const isSuperUser = roles.includes("SuperUser");

  const [tab, setTab] = useState<"workbench" | "analytics">("workbench");
  const [filters, setFilters] = useState<AuditFilterValues>(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<AuditFilterValues>(filters);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const [reloadKey, setReloadKey] = useState(0);

  const [rows, setRows] = useState<DuplicateAuditListItem[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [summary, setSummary] = useState<DuplicateAuditSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [lookups, setLookups] = useState<DuplicateReviewLookups | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ReviewFieldErrors>({});
  const [toast, setToast] = useState<{
    message: string;
    severity: "success" | "error" | "warning";
  } | null>(null);
  const investigationRef = useRef<HTMLDivElement>(null);

  const rangeInvalid =
    !!filters.from && !!filters.to && filters.from.isAfter(filters.to, "day");

  // Debounce filter changes (mainly text inputs), then go back to the first page.
  useEffect(() => {
    if (rangeInvalid) return;
    const handle = setTimeout(() => {
      setAppliedFilters(filters);
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [filters, rangeInvalid]);

  // Lookups are loaded once when the screen opens.
  useEffect(() => {
    if (!rolesLoaded || !isSuperUser) return;
    callApi(getApiUrl(API_CONFIG.ENDPOINTS.DUPLICATE_AUDIT_LOOKUPS))
      .then((res) => setLookups(res.data as DuplicateReviewLookups))
      .catch(() => setToast({ message: "Failed to load review options.", severity: "error" }));
  }, [callApi, rolesLoaded, isSuperUser]);

  useEffect(() => {
    if (!rolesLoaded || !isSuperUser) return;
    const controller = new AbortController();
    setLoading(true);
    callApi(
      `${getApiUrl(API_CONFIG.ENDPOINTS.DUPLICATE_AUDITS)}?${buildQuery(
        appliedFilters,
        paginationModel,
        sortModel
      )}`,
      { signal: controller.signal }
    )
      .then((res) => {
        const data = res.data as DuplicateAuditListResponse;
        setRows(data.audits.items);
        setTotalRows(data.audits.totalCount);
        setSummary(data.summary);
        setForbidden(false);
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          setForbidden(true);
        } else {
          const first = (err?.response?.data as ApiErrors | undefined)?.errors?.[0];
          setToast({
            message: first?.error ?? "Failed to load duplicate audits.",
            severity: "error",
          });
        }
        setLoading(false);
      });
    return () => controller.abort();
  }, [callApi, rolesLoaded, isSuperUser, appliedFilters, paginationModel, sortModel, reloadKey]);

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model.length ? model : DEFAULT_SORT_MODEL);
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const selected = useMemo(
    () => rows.find((r) => r.auditId === selectedId) ?? null,
    [rows, selectedId]
  );

  const handleSelect = (row: DuplicateAuditListItem) => {
    setSelectedId(row.auditId);
    setFieldErrors({});
    setTimeout(
      () => investigationRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      0
    );
  };

  const handleSave = useCallback(
    async (request: SaveDuplicateAuditReviewRequest) => {
      if (selectedId === null) return;
      setSaving(true);
      setFieldErrors({});
      try {
        const res = await callApi(
          getApiUrl(
            API_CONFIG.ENDPOINTS.DUPLICATE_AUDIT_REVIEW.replace("{id}", String(selectedId))
          ),
          { method: "PUT", data: request }
        );
        // Keep the new rowVersion locally, otherwise the next save would get a 409.
        setRows((prev) =>
          prev.map((r) =>
            r.auditId === selectedId ? { ...r, isReviewed: true, review: res.data } : r
          )
        );
        setReloadKey((k) => k + 1); // refresh pending count / status filter
        setToast({ message: "Review saved.", severity: "success" });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 409) {
          // Never retry automatically: reload so the user sees the other reviewer's values.
          setToast({
            message:
              "This review was modified by another user. The latest values have been reloaded - please re-apply your change.",
            severity: "warning",
          });
          setReloadKey((k) => k + 1);
        } else if (axios.isAxiosError(err) && err.response?.status === 400) {
          const errors = (err.response.data as ApiErrors).errors ?? [];
          const mapped: ReviewFieldErrors = {};
          errors.forEach((e) => {
            if (e.field === "outcomeId" || e.field === "rootCauseId" || e.field === "actionId") {
              mapped[e.field] = e.error;
            }
          });
          setFieldErrors(mapped);
          setToast({
            message: errors.map((e) => e.error).join(" ") || "Review could not be saved.",
            severity: "error",
          });
          if (errors.some((e) => e.field === "rowVersion")) setReloadKey((k) => k + 1);
        } else {
          setToast({ message: "Failed to save the review.", severity: "error" });
        }
      } finally {
        setSaving(false);
      }
    },
    [callApi, selectedId]
  );

  if (!rolesLoaded) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuperUser || forbidden) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        You do not have access to the Duplicate Audit Review. This screen is available to Super Users only.
      </Alert>
    );
  }

  return (
    <Box sx={{ pl: 3, display: "flex", flexDirection: "column", gap: spacing.mui.lg, pb: 4 }}>
      <Box>
        <Typography variant="h4" fontWeight={700}>
          Customer Duplicate Investigation Workbench
        </Typography>
        <Typography variant="body2" color={colors.text.secondary}>
          Review duplicate audits and record outcomes
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab value="workbench" label="Review Workbench" />
        <Tab value="analytics" label="Training Analytics" />
      </Tabs>

      {tab === "analytics" && (
        <Paper
          elevation={elevation.low}
          sx={{ p: spacing.mui.xl, borderRadius: borderRadius.mui.medium }}
        >
          <Typography variant="h6" fontWeight={700}>
            Training Analytics
          </Typography>
          <Typography color={colors.text.secondary}>Work in progress.</Typography>
        </Paper>
      )}

      {tab === "workbench" && (
        <>
          <AuditSummaryTiles summary={summary} />
          <AuditFilters
            values={filters}
            onChange={setFilters}
            onClear={() => setFilters(getDefaultFilters())}
          />
          <Box>
            <Typography variant="h6" fontWeight={700} mb={spacing.mui.sm}>
              Duplicate Review Queue
            </Typography>
            <AuditQueueGrid
              rows={rows}
              totalRows={totalRows}
              loading={loading}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              selectedId={selectedId}
              onSelect={handleSelect}
            />
          </Box>
          <Box ref={investigationRef}>
            {selected && (
              <AuditInvestigationPanel
                audit={selected}
                lookups={lookups}
                saving={saving}
                fieldErrors={fieldErrors}
                onSave={handleSave}
              />
            )}
          </Box>
        </>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {toast ? (
          <Alert onClose={() => setToast(null)} severity={toast.severity} sx={{ width: "100%" }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
